import { Link } from '@tanstack/react-router';
import { ArrowLeftRight, ExternalLink, FileText, MoreHorizontal } from 'lucide-react';
import type { ReactNode } from 'react';

import { IconHint } from '../../../chrome/hint';
import { Button } from '../../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../ui/dropdown-menu';
import { useSetRelatedness } from '../api/queries';
import type { PostRow } from '../api/schemas';
import { networkLabel } from '../publication/messages';
import { ds } from './messages';

/** What the ⋯ menu needs beyond the post: where it writes, and whether the
 *  reader may. */
export interface CardMenu {
  campaignId: string;
  tenantId: string;
  canReclassify: boolean;
}

/**
 * The ⋯ menu a post carries on the feed card and in the post header: open the
 * post (where a destination is given), view it on its network, and flip
 * whether it counts for the campaign. The flip is an override of this post's
 * classification, offered only to a reader who may write.
 */
export function PostMenu(props: {
  post: PostRow;
  campaignId: string;
  tenantId: string;
  /** The effective flag, override included. */
  related: boolean;
  canReclassify: boolean;
  href?: string;
}): ReactNode {
  const { post } = props;
  const reclassify = useSetRelatedness(props.campaignId, props.tenantId);
  return (
    <DropdownMenu>
      <IconHint label={ds('postMenu.open')}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon-sm">
            <MoreHorizontal aria-hidden />
          </Button>
        </DropdownMenuTrigger>
      </IconHint>
      <DropdownMenuContent align="end">
        {props.href !== undefined && (
          <DropdownMenuItem asChild>
            <Link to={props.href}>
              <FileText aria-hidden />
              {ds('postMenu.details')}
            </Link>
          </DropdownMenuItem>
        )}
        {post.url !== null && (
          <DropdownMenuItem asChild>
            <a href={post.url} target="_blank" rel="noopener">
              <ExternalLink aria-hidden />
              {ds('postMenu.source', { network: networkLabel(post.platform) })}
            </a>
          </DropdownMenuItem>
        )}
        {props.canReclassify && (
          <DropdownMenuItem
            disabled={reclassify.isPending}
            onSelect={() => reclassify.mutate({ postId: post.post_id, related: !props.related })}
          >
            <ArrowLeftRight aria-hidden />
            {ds(props.related ? 'postMenu.markUnrelated' : 'postMenu.markRelated')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
